import {
  expectedWindow,
  massOf,
  supportSize,
  validateForecast,
  validatePmf,
  validateTiming,
  type ForecastInProgress,
} from "@sooth-labs/idl";
import {
  filterAndStampEvents,
  type Contract,
  type ForecastEvent,
  type ProgressActivity,
} from "./model.js";
import { mayBeSeedCall } from "./seed.js";
import { eventKey } from "./eventIdentity.js";

const optionalText = (source: Record<string, unknown>, key: string): string | undefined =>
  typeof source[key] === "string" ? (source[key] as string) : undefined;
const SAFE_SOURCE_PROTOCOLS = new Set(["http:", "https:"]);
const sourceUrl = (source: Record<string, unknown>): string | undefined => {
  const value = optionalText(source, "source_url") ?? optionalText(source, "source");
  if (!value) return undefined;
  try {
    return SAFE_SOURCE_PROTOCOLS.has(new URL(value).protocol) ? value : undefined;
  } catch {
    return undefined;
  }
};
const validProbability = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/;
const validPlainDate = (value: unknown): value is string => {
  if (typeof value !== "string" || !PLAIN_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

/** Project the folded IDL document into the established timeline event shape. */
export function projectLiveEvents(
  doc: ForecastInProgress,
  now: Date = new Date(),
): ForecastEvent[] {
  const past = doc.pastTriggers.flatMap<ForecastEvent>((trigger): ForecastEvent[] => {
    if (typeof trigger.title !== "string") return [];
    if (!validPlainDate(trigger.occurred_on)) return [];
    if (!doc.space || !Number.isInteger(trigger.moved_toward) || trigger.moved_toward < 0)
      return [];
    try {
      if (trigger.moved_toward >= supportSize(doc.space)) return [];
    } catch {
      return [];
    }
    const extra = trigger as unknown as Record<string, unknown>;
    const binary = doc.space?.kind === "boolean";
    const decisiveness =
      typeof extra.decisiveness === "number" &&
      Number.isFinite(extra.decisiveness) &&
      extra.decisiveness >= 0.5 &&
      extra.decisiveness <= 1
        ? extra.decisiveness
        : null;
    return [
      {
        id: trigger.id,
        title: trigger.title,
        date: trigger.occurred_on,
        occurred: true,
        direction: binary ? (trigger.moved_toward === 1 ? "yes" : "no") : null,
        p_event: 1,
        // `decisiveness` is an additive producer field, so older streams omit it.
        // Project valid values but never synthesize conditional geometry when absent.
        p_conditional: binary ? decisiveness : null,
        description: optionalText(extra, "description"),
        source_url: sourceUrl(extra),
        _live: true,
        ...(!binary ? { _neutral: true } : {}),
      } satisfies ForecastEvent,
    ];
  });

  const future = doc.futureTriggers.flatMap<ForecastEvent>((trigger): ForecastEvent[] => {
    try {
      if (!Array.isArray(trigger.when) || trigger.when.length === 0) return [];
      if (typeof trigger.title !== "string") return [];
      if (
        !trigger.when.every((slice) => validPlainDate(slice.starts) && validPlainDate(slice.ends))
      )
        return [];
      if (!trigger.when.every((slice) => validProbability(slice.mass))) return [];
      const extra = trigger as unknown as Record<string, unknown>;
      validateTiming(trigger.when);
      const window = expectedWindow(trigger.when);
      const base: ForecastEvent = {
        id: trigger.id,
        title: trigger.title,
        date: window.starts,
        ...(window.ends !== window.starts ? { date_end: window.ends } : {}),
        direction: null,
        p_conditional: null,
        description: optionalText(extra, "description"),
        source_url: sourceUrl(extra),
        _live: true,
      };
      if (!trigger.score || !doc.forecast) return [{ ...base, _neutral: true }];

      const score = trigger.score;
      if (!validProbability(score.p_occurs)) return [];
      if (doc.space?.kind === "boolean" && score.if_occurs.form === "bernoulli") {
        if (doc.forecast.form !== "bernoulli") return [];
        const p0 = massOf(doc.forecast)[1]!;
        const a = score.if_occurs.probability;
        const b = score.if_not.form === "bernoulli" ? score.if_not.probability : null;
        if (!validProbability(p0) || !validProbability(a) || !validProbability(b)) return [];
        const direction = a > p0 ? "yes" : "no";
        return [
          {
            ...base,
            p_event: score.p_occurs,
            a,
            b,
            direction,
            p_conditional: direction === "yes" ? a : 1 - a,
          },
        ];
      }

      if (score.if_occurs.form === "pmf" && score.if_not.form === "pmf") {
        if (!doc.space || doc.forecast.form !== "pmf") return [];
        validateForecast(doc.space, doc.forecast);
        validatePmf(score.if_occurs.mass, doc.space);
        validatePmf(score.if_not.mass, doc.space);
        const anchor = massOf(doc.forecast);
        return [
          {
            ...base,
            p_event: score.p_occurs,
            a: null,
            b: null,
            a_pmf: [...score.if_occurs.mass],
            b_pmf: [...score.if_not.mass],
            dist_impact:
              0.5 *
              score.if_occurs.mass.reduce(
                (sum, value, i) => sum + Math.abs(value - (anchor[i] ?? value)),
                0,
              ),
            _neutral: true,
          },
        ];
      }
      return [{ ...base, _neutral: true }];
    } catch {
      return [];
    }
  });

  return filterAndStampEvents([...past, ...future], now);
}

const geometryKey = (event: ForecastEvent): string =>
  JSON.stringify([
    event.date,
    event.date_end ?? null,
    event.mean_date ?? null,
    event._gradingWindow === true,
    event.isPast === true,
  ]);

/** Options for {@link liveEventsReconcileInPlace}. */
export interface ReconcileInPlaceOptions {
  /**
   * Reforecast shape. The landed timeline is the ACCUMULATED one: prior-vintage past
   * events the live projection never carried, plus the fresh vintage under its own ids — and
   * the accumulate-past dedup may keep an established old copy over a live past node. The
   * fresh-launch equal-set check therefore never holds on a reforecast. Instead, every live
   * node whose id SURVIVES is held to the geometry check; an unmatched live node is removed,
   * not replaced (its cache entry is orphaned, never stale for a surviving node), and an extra
   * authoritative node simply gets a fresh anchor. At least one live id must survive.
   */
  accumulated?: boolean;
}

/** Whether authoritative events can replace live nodes without moving their cached anchors. */
export function liveEventsReconcileInPlace(
  liveEvents: readonly ForecastEvent[],
  authoritativeEvents: readonly ForecastEvent[],
  { accumulated = false }: ReconcileInPlaceOptions = {},
): boolean {
  if (liveEvents.length === 0) return false;
  if (!accumulated && liveEvents.length !== authoritativeEvents.length) return false;
  // Matched on `occurrenceKey`, NOT on `id`. A run renumbers its triggers from `e1`
  // every time, so the accumulated timeline legitimately holds two different events
  // under the same id (an old vintage's `e3` and this run's `e3`) — an id map would
  // see a duplicate and bail. `occurrenceKey` is the cross-vintage handle, and the
  // live projection and the landed timeline compute it identically, which is what
  // lets a live node survive `done` instead of remounting.
  const authoritativeByKey = new Map<string, ForecastEvent>();
  for (const event of authoritativeEvents) {
    const key = eventKey(event);
    if (!key || authoritativeByKey.has(key)) return false;
    authoritativeByKey.set(key, event);
  }
  const liveKeys = new Set<string>();
  let matched = 0;
  for (const event of liveEvents) {
    const key = eventKey(event);
    if (!key || liveKeys.has(key)) return false;
    liveKeys.add(key);
    const authoritative = authoritativeByKey.get(key);
    if (authoritative == null) {
      if (!accumulated) return false;
      continue;
    }
    if (geometryKey(event) !== geometryKey(authoritative)) return false;
    matched += 1;
  }
  return matched > 0;
}

/**
 * Project the live contract metadata into the contract shape the lane consumes.
 *
 * Metadata only, never the in-flight estimate: a mid-run forecast (the seed's, or a
 * revision) on the resolution marker reads as the final answer before the run has
 * finished, and skips the landing reveal the completed forecast animates in with.
 */
export function projectLiveHeadline(doc: ForecastInProgress): Contract | null {
  const contract =
    doc.contract && typeof doc.contract === "object"
      ? (doc.contract as unknown as Record<string, unknown>)
      : null;
  const question = contract?.question;
  const resolutionCriteria = contract?.resolution_criteria;
  const resolutionDate = contract?.resolution_date;
  if (
    typeof question !== "string" ||
    typeof resolutionCriteria !== "string" ||
    !validPlainDate(resolutionDate)
  )
    return null;
  return {
    question,
    resolution_criteria: resolutionCriteria,
    resolution_date: resolutionDate,
    _live: true,
  };
}

/** Human-readable one-line research activity; null deliberately renders nothing. */
export function progressStatus(activity: ProgressActivity | null, _tail: boolean): string | null {
  if (!activity) return null;
  // Before the title fallback below, which would print the producer's own
  // title — and that names the model we seed with. Null rather than a phrase:
  // `progressActivity` is only replaced by the NEXT `tool_call_started`, so any
  // wording here would still read as live long after the chat reported the
  // seed's answer.
  if (mayBeSeedCall(activity.callId, activity.detail)) return null;
  if (activity.tool === "write_evidence_card") {
    const count = activity.detail.card_count;
    const latest = activity.detail.source ?? activity.title;
    if (typeof count === "number" && typeof latest === "string") {
      return `reading ${count} sources · latest: ${latest}`;
    }
  }
  return typeof activity.title === "string" ? activity.title : null;
}

/** The pending lane status, with a real tail report taking precedence. */
export function liveStatus(
  doc: ForecastInProgress,
  activity: ProgressActivity | null,
  tail: boolean,
): string | null {
  if (tail && typeof doc.report?.summary === "string") return doc.report.summary;
  return progressStatus(activity, tail);
}
